import { ClientOnly } from "@tanstack/react-router";
import { format, startOfMonth } from "date-fns";
import { Clock, Crown, Medal, Trophy, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type RingRankPageProps = {
  rank: RingRankRecord[];
  period: "month" | "all";
  onPeriodChange: (period: "month" | "all") => void;
};

type RingRankRecord = {
  id: string;
  score: number;
  createdAt: Date | string;
  user?: {
    name: string | null;
  } | null;
};

export function RingRankPage({ rank, period, onPeriodChange }: RingRankPageProps) {
  const latestRecordAt =
    rank.length > 0
      ? new Date(Math.max(...rank.map((item) => normalizeDate(item.createdAt).getTime())))
      : null;

  return (
    <div className="py-4">
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            游戏排行榜
          </h1>
        </div>
        <Badge variant="outline" className="text-sm">
          版本 V1
        </Badge>
      </div>

      {rank.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                参与玩家
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">{rank.length}</div>
              <p className="text-xs text-muted-foreground">总参与人数</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                最高分数
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ClientOnly>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 font-mono">
                  {rank[0]?.score.toLocaleString() || 0}
                </div>
              </ClientOnly>
              <p className="text-xs text-muted-foreground">{rank[0]?.user?.name || "暂无"}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" />
                最新记录
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ClientOnly>
                <div className="text-2xl font-bold font-mono">
                  {latestRecordAt ? formatMonthDay(latestRecordAt) : "--"}
                </div>
              </ClientOnly>
              <p className="text-xs text-muted-foreground">最近更新时间</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            <span>排行榜</span>

            <div className="flex-1" />

            <ClientOnly>
              <p className="font-mono text-sm">
                {period === "month" ? format(startOfMonth(new Date()), "yyyy年MM月") : "不限月份"}
              </p>
            </ClientOnly>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={period === "month" ? "default" : "outline"}
              onClick={() => onPeriodChange("month")}
            >
              月榜
            </Button>
            <Button
              size="sm"
              variant={period === "all" ? "default" : "outline"}
              onClick={() => onPeriodChange("all")}
            >
              总榜
            </Button>
          </div>
          <CardDescription>
            <p>
              根据游戏分数排序，展示所有玩家的成绩。
              {period === "month" ? "当前展示本月内成绩" : "当前展示的总榜成绩"}
            </p>
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2">
                  <TableHead className="w-24 font-semibold">排名</TableHead>
                  <TableHead className="font-semibold">玩家</TableHead>
                  <TableHead className="text-right font-semibold">分数</TableHead>
                  <TableHead className="text-right font-semibold min-w-[140px]">游戏时间</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rank.map((item, index) => {
                  const rankNumber = index + 1;
                  const rankIcon = getRankIcon(rankNumber);
                  const styles = getRankStyles(rankNumber);
                  return (
                    <TableRow key={item.id} className={styles.row}>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-3">
                          {rankIcon}
                          {rankNumber > 3 && (
                            <p
                              className={cn(
                                "font-bold min-w-[40px] justify-center font-mono text-sm ml-1",
                                styles.text,
                              )}
                            >
                              #{rankNumber}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex flex-col">
                          <span className={cn("text-base", styles.text)}>
                            {item.user?.name || "匿名用户"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right py-4">
                        <ClientOnly>
                          <p className={cn("font-mono text-base font-bold", styles.score)}>
                            {item.score.toLocaleString()}
                          </p>
                        </ClientOnly>
                      </TableCell>
                      <TableCell className="text-right py-4 text-sm text-muted-foreground">
                        <ClientOnly>
                          <div className="flex flex-col items-end font-mono">
                            <span>{formatMonthDay(item.createdAt)}</span>
                            <span className="text-xs">{formatHourMinute(item.createdAt)}</span>
                          </div>
                        </ClientOnly>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {rank.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-12">
                      <div className="flex flex-col items-center gap-3">
                        <Trophy className="w-12 h-12 text-muted-foreground/50" />
                        <div>
                          <p className="text-lg font-medium text-muted-foreground">
                            暂无排行榜数据
                          </p>
                          <p className="text-sm text-muted-foreground/70">
                            成为第一个上榜的玩家吧！
                          </p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function getRankIcon(rank: number) {
  if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-slate-500 dark:text-slate-400" />;
  if (rank === 3) return <Trophy className="w-5 h-5 text-amber-600 dark:text-amber-500" />;
  return null;
}

function getRankStyles(rank: number) {
  const baseStyles = "transition-all duration-200";

  if (rank === 1) {
    return {
      row: `${baseStyles} bg-gradient-to-r from-yellow-50/50 to-amber-50/50 dark:from-yellow-950/90 dark:to-amber-950/20 border-l-4 border-l-yellow-400`,
      text: "text-yellow-700 dark:text-yellow-300 font-bold",
      score: "text-yellow-800 dark:text-yellow-200",
    };
  }

  if (rank === 2) {
    return {
      row: `${baseStyles} bg-gradient-to-r from-slate-50/50 to-gray-50/50 dark:from-slate-500/30 dark:to-gray-950/20 border-l-4 border-l-slate-400`,
      text: "text-slate-700 dark:text-slate-300 font-bold",
      score: "text-slate-800 dark:text-slate-200",
    };
  }

  if (rank === 3) {
    return {
      row: `${baseStyles} bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-950/50 dark:to-orange-950/20 border-l-4 border-l-amber-500`,
      text: "text-amber-700 dark:text-amber-300 font-bold",
      score: "text-amber-800 dark:text-amber-200",
    };
  }

  if (rank <= 10) {
    return {
      row: `${baseStyles} hover:bg-muted/50 border-l-2 border-l-primary/30`,
      text: "text-primary font-semibold",
      score: "text-primary",
    };
  }

  return {
    row: `${baseStyles} hover:bg-muted/30`,
    text: "text-foreground",
    score: "text-muted-foreground",
  };
}

function normalizeDate(dateLike: Date | string) {
  return dateLike instanceof Date ? dateLike : new Date(dateLike);
}

function formatMonthDay(dateLike: Date | string) {
  return format(normalizeDate(dateLike), "MM月dd日");
}

function formatHourMinute(dateLike: Date | string) {
  return format(normalizeDate(dateLike), "HH:mm");
}
